import { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
  useInView,
} from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

// Global scroll-reveal: rise + fade + de-blur for a premium feel
export const fadeUp = {
  hidden: { opacity: 0, y: 52, filter: "blur(10px)" },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.9, delay: i * 0.09, ease: EASE },
  }),
};

export const Reveal = ({ children, className = "", delay = 0, as = "div" }) => {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      variants={fadeUp}
      custom={delay}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-80px" }}
    >
      {children}
    </M>
  );
};

// Masked line reveal — used on scroll into view
export const MaskLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      whileInView={{ y: "0%" }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

// Continuous parallax: element drifts as it moves through the viewport
export const Parallax = ({ children, speed = 0.15, className = "", as = "div" }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [speed * 120, -speed * 120]);
  const M = motion[as] || motion.div;
  return (
    <M ref={ref} style={reduce ? undefined : { y }} className={className}>
      {children}
    </M>
  );
};

// Top-of-page scroll progress bar
export const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.3,
  });
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[3px] origin-left z-[70] bg-gradient-to-r from-teal via-cobalt to-magenta"
      data-testid="scroll-progress"
    />
  );
};

// Animated count-up when scrolled into view
export const Counter = ({ to, duration = 1.6, suffix = "", prefix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduce = useReducedMotion();
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setVal(to);
      return;
    }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration, reduce]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {val}
      {suffix}
    </span>
  );
};

// "Book a call" now routes to the dedicated /book page (GHL booking widget).
export const openBooking = () => {
  if (typeof window !== "undefined") {
    if (window.location.pathname === "/book") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.location.assign("/book");
    }
  }
};

export const scrollToId = (id) => {
  const el = document.querySelector(id);
  if (!el) {
    // not on the landing page — go home and jump to the section
    window.location.href = `/${id}`;
    return;
  }
  if (typeof window !== "undefined" && window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -70 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};
